import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  FormArray
} from '@angular/forms';
import { IOrder, IOrderLineItem } from '@care-portals/api-interfaces';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { INSURANCE_TYPES } from '@care-portals/data';
import { Subject, takeUntil } from 'rxjs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'portals-emr-order-editor',
  templateUrl: './order-editor.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputNumberModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzTagModule,
    NzTableModule,
    NzToolTipModule
  ]
})
export class OrderEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() order: IOrder;
  @Output() productsSave = new EventEmitter<Partial<IOrder>>();
  @Output() formValidityChange = new EventEmitter<boolean>();

  orderForm: FormGroup;
  insuranceTypes = INSURANCE_TYPES;
  isLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private notification: NzNotificationService
  ) {
    this.orderForm = this.fb.group({
      items: this.fb.array([])
    });
  }

  get itemsArray(): FormArray {
    return this.orderForm.get('items') as FormArray;
  }

  get products(): IOrderLineItem[] {
    return this.order.lineItems;
  }

  ngOnInit(): void {
    this.initializeProductForms();
    this.setupFormValiditySubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order']) {
      this.initializeProductForms();
      this.setupFormValiditySubscription();
    }
  }

  initializeProductForms() {
    const itemsArray = this.itemsArray;

    // Clear existing forms
    while (itemsArray.length !== 0) {
      itemsArray.removeAt(0);
    }

    if (!this.order || this.order.lineItems.length === 0) {
      return;
    }

    const formGroups = this.order.lineItems.map((product, index) => {
      const insuranceCoverages = this.fb.array(
        (product.insuranceCoverages || []).map(insurance =>
          this.fb.group({
            type: [insurance.type, Validators.required],
            amount: [
              insurance.amount,
              [Validators.required, Validators.min(0), this.decimalValidator]
            ]
          })
        )
      );

      const formGroup = this.fb.group({
        salePrice: [
          product.salePrice || product.listPrice || 0,
          [Validators.required, this.decimalValidator]
        ],
        quantity: [product.quantity, [Validators.required, Validators.min(1)]],
        drugPrice: [
          product.drugPrice || 0,
          [this.decimalValidator, this.drugPriceValidator]
        ],
        discount: [
          product.discount?.amount || 0,
          [this.decimalValidator, this.discountValidator]
        ],
        insuranceCoverages: insuranceCoverages
      });

      // Set up live validations for cross-field dependencies
      this.setupLiveValidations(formGroup, index);

      return formGroup;
    });

    formGroups.forEach(formGroup => {
      this.itemsArray.push(formGroup);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormValiditySubscription(): void {
    this.orderForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formValidityChange.emit(this.itemsArray.valid);
    });
  }

  private setupLiveValidations(formGroup: FormGroup, index: number): void {
    // Subscribe to changes that affect cross-field validations
    const salePrice$ = formGroup.get('salePrice')?.valueChanges;
    const quantity$ = formGroup.get('quantity')?.valueChanges;
    const drugPrice$ = formGroup.get('drugPrice')?.valueChanges;

    // Update validations when salePrice or quantity changes
    if (salePrice$) {
      salePrice$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        formGroup
          .get('drugPrice')
          ?.updateValueAndValidity({ emitEvent: false });
        formGroup.get('discount')?.updateValueAndValidity({ emitEvent: false });
        this.updateInsuranceValidations(index);
      });
    }

    // Update validations when quantity changes
    if (quantity$) {
      quantity$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        formGroup
          .get('drugPrice')
          ?.updateValueAndValidity({ emitEvent: false });
        formGroup.get('discount')?.updateValueAndValidity({ emitEvent: false });
        this.updateInsuranceValidations(index);
      });
    }

    // Update discount validation when drugPrice changes
    if (drugPrice$) {
      drugPrice$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        formGroup.get('discount')?.updateValueAndValidity({ emitEvent: false });
        this.updateInsuranceValidations(index);
      });
    }
  }

  addInsurance(index: number) {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    const insuranceCoverages = formGroup.get('insuranceCoverages') as FormArray;

    insuranceCoverages.push(
      this.fb.group({
        type: ['other', Validators.required],
        amount: [
          0,
          [
            Validators.required,
            Validators.min(0),
            this.decimalValidator,
            this.insuranceValidator(index)
          ]
        ]
      })
    );
    this.updateInsuranceValidations(index);
  }

  removeInsurance(index: number, insuranceIndex: number) {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    const insuranceCoverages = formGroup.get('insuranceCoverages') as FormArray;
    insuranceCoverages.removeAt(insuranceIndex);
    this.updateInsuranceValidations(index);
  }

  onDecimalInput(event: any, index: number, field: string) {
    const value = event.target.value;
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1].length > 2) {
        const formGroup = this.itemsArray.at(index) as FormGroup;
        formGroup
          .get(field)
          ?.setValue(Number(parts[0] + '.' + parts[1].substring(0, 2)));
      }
    }
  }

  updateInsuranceValidations(index: number) {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    const insuranceCoverages = formGroup.get('insuranceCoverages') as FormArray;
    formGroup.get('drugPrice')?.updateValueAndValidity();
    formGroup.get('discount')?.updateValueAndValidity();
    insuranceCoverages.controls.forEach(insuranceCtrl => {
      insuranceCtrl.get('amount')?.updateValueAndValidity();
    });
  }

  // Validation methods
  private decimalValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const value = control.value.toString();
    const decimalPlaces = value.includes('.') ? value.split('.')[1].length : 0;
    return decimalPlaces > 2
      ? { tooManyDecimals: { max: 2, actual: decimalPlaces } }
      : null;
  }

  private drugPriceValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const salePrice = parent.get('salePrice')?.value || 0;
    const quantity = parent.get('quantity')?.value || 0;
    const totalPrice = salePrice * quantity;

    if (control.value > totalPrice) {
      return {
        drugPriceExceedsSalePrice: { max: totalPrice, actual: control.value }
      };
    }
    return null;
  }

  private discountValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const salePrice = parent.get('salePrice')?.value || 0;
    const quantity = parent.get('quantity')?.value || 0;
    const drugPrice = parent.get('drugPrice')?.value || 0;
    const totalPrice = salePrice * quantity;
    const maxDiscount = totalPrice - drugPrice;

    if (control.value > maxDiscount) {
      return {
        discountExceedsMax: { max: maxDiscount, actual: control.value }
      };
    }
    return null;
  }

  private insuranceValidator(index: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;

      const formGroup = this.itemsArray.at(index) as FormGroup;
      if (!formGroup) return null;

      const drugPrice = formGroup.get('drugPrice')?.value || 0;
      const quantity = formGroup.get('quantity')?.value || 0;
      const totalDrugPrice = drugPrice * quantity;

      const insuranceCoverages = formGroup.get(
        'insuranceCoverages'
      ) as FormArray;
      const currentInsuranceTotal = insuranceCoverages.controls.reduce(
        (total, insuranceCtrl) => {
          return total + (insuranceCtrl.get('amount')?.value || 0);
        },
        0
      );

      return currentInsuranceTotal > totalDrugPrice
        ? {
            insuranceExceedsPrice: {
              max: totalDrugPrice,
              actual: currentInsuranceTotal
            }
          }
        : null;
    };
  }

  isInsuranceMaxed(index: number): boolean {
    const item = this.itemsArray.at(index) as FormGroup;
    if (!item) return false;
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    if (insuranceCoverages.length === 0) return false;

    const drugPrice = item.get('drugPrice').value;
    const quantity = item.get('quantity').value;
    const totalPrice = drugPrice * quantity;

    const currentInsuranceTotal = insuranceCoverages.controls.reduce(
      (total, insuranceCtrl) => {
        return total + (insuranceCtrl.get('amount')?.value || 0);
      },
      0
    );

    return currentInsuranceTotal > totalPrice;
  }

  hasFieldError(index: number, field: string): boolean {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    const control = formGroup?.get(field);
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  getFieldError(index: number, field: string): string {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    const control = formGroup?.get(field);

    if (!control?.errors) return '';

    if (control.errors['required']) return 'This field is required';
    if (control.errors['min'])
      return `Minimum value is ${control.errors['min'].min}`;
    if (control.errors['tooManyDecimals']) return 'Too many decimal places';
    if (control.errors['subscriptionQuantityExceeded'])
      return 'Subscription products cannot have quantity greater than 1';
    if (control.errors['drugPriceExceedsSalePrice'])
      return 'Drug price cannot exceed sale price';
    if (control.errors['discountExceedsMax'])
      return 'Discount cannot exceed max discount';

    return '';
  }

  cancelEdit() {
    this.initializeProductForms();
    this.formValidityChange.emit(this.itemsArray.valid);
  }

  get isValid(): boolean {
    return this.itemsArray.valid;
  }

  saveChanges() {
    if (!this.itemsArray.valid) {
      this.notification.error(
        'Error',
        'Please fix all validation errors before saving.'
      );
      return;
    }

    let totalInsuranceAmount = 0;
    let subTotalAmount = 0;
    let discountAmount = 0;

    const lineItems = this.order.lineItems.map((product, index) => {
      const formGroup = this.itemsArray.at(index) as FormGroup;
      const formValue = formGroup.value;

      const discount = formValue.discount || 0;
      const insurance = formValue.insuranceCoverages.reduce(
        (acc, insurance) => acc + insurance.amount,
        0
      );
      const lineSubTotal = formValue.salePrice * formValue.quantity;
      const price = lineSubTotal - discount - insurance;

      totalInsuranceAmount += insurance;
      subTotalAmount += lineSubTotal;
      discountAmount += discount;

      return {
        ...product,
        price,
        salePrice: formValue.salePrice,
        quantity: formValue.quantity,
        drugPrice: formValue.drugPrice > 0 ? formValue.drugPrice : undefined,
        discount:
          formValue.discount > 0
            ? {
                type: 'manual' as const,
                amount: formValue.discount,
                reductionAmount: formValue.discount,
                reductionType: 'dollar' as const,
                referenceId: product.discount?.referenceId || null
              }
            : undefined,
        insuranceCoverages: formValue.insuranceCoverages.map(insurance => ({
          type: insurance.type,
          amount: insurance.amount
        })),
        totalInsuranceAmount:
          Math.round(
            formValue.insuranceCoverages.reduce(
              (acc, insurance) => acc + insurance.amount,
              0
            ) * 100
          ) / 100
      };
    });

    // Only change discount amount if changed manually, otherwise keep order discount amount.
    const appliedDiscountAmount = discountAmount || this.order.discountAmount;

    const totalAmount =
      subTotalAmount - appliedDiscountAmount - totalInsuranceAmount;

    const updatedOrder = {
      totalAmount,
      totalInsuranceAmount,
      subTotalAmount,
      discountAmount: appliedDiscountAmount,
      lineItems
    };

    this.productsSave.emit(updatedOrder);
  }

  calculateProductTotal(index: number): number {
    const formGroup = this.itemsArray.at(index) as FormGroup;
    if (!formGroup) {
      return 0;
    }
    const formValue = formGroup.value;
    const quantity = formValue.quantity || 0;
    const salePrice = formValue.salePrice || 0;
    const discount = formValue.discount || 0;
    const insuranceTotal =
      formValue.insuranceCoverages?.reduce(
        (acc, insurance) => acc + (insurance.amount || 0),
        0
      ) || 0;

    return quantity * salePrice - discount - insuranceTotal;
  }

  getTotalInsuranceAmount(product: any): number {
    if (!product.insuranceCoverages) return 0;
    return product.insuranceCoverages.reduce(
      (total, insurance) => total + (insurance.amount || 0),
      0
    );
  }

  getInsuranceArray(productIndex: number): FormArray {
    const formGroup = this.itemsArray.at(productIndex) as FormGroup;
    return formGroup.get('insuranceCoverages') as FormArray;
  }

  allowDiscountEdit(): boolean {
    // if there is a coupon applied, discount cannot be edited
    if (this.order.coupon) {
      return false;
    }

    // if there is a manual discount applied, discount can be edited
    if (this.order.lineItems.some(item => item.discount?.type === 'manual')) {
      return true;
    }

    // If there is no manual discount applied but total discount is greater than 0, discount cannot be edited
    // Means a cart level discount was applied
    if (this.order.discountAmount > 0) {
      return false;
    }

    return true;
  }
}
